/**
 * Product-Group Availability Bell — HIG-152 (Tobias 2026-06-09)
 * ------------------------------------------------------------------
 * Mirrors variant-availability.js's contextual sold-out bell, but ACROSS products.
 * A product-group member swatch is a SEPARATE product rendered as an <a>; it gets a
 * sold-out bell when the option value currently selected on THIS product (e.g.
 * Grösse = S) exists for that member but is sold out there — exactly like picking a
 * size that's OOS in one colour of a single multi-variant product.
 *
 * Conservative by design (Tobias's explicit preference: rather under-show OOS than
 * risk a false "sold out"):
 *   - member doesn't carry that option/value   -> NO bell (stays clickable)
 *   - member has the value available somewhere  -> NO bell
 *   - member has the value but every match OOS  -> bell
 * It never marks a member sold-out that a customer could still buy in that size.
 *
 * Why a separate `hp-pg-oos` class (not `is-unavailable`): product-group swatches are
 * ADJACENT .opt-label siblings, so the variant rules `.is-unavailable + .opt-label`
 * would bleed the badge onto the NEXT swatch. A distinct class stays fully isolated.
 *
 * Data: each <fieldset.option-selector--product-group> carries a
 *   <script type="application/json" data-pg-availability>
 *     { "<memberId>": { "names":[optName...], "variants":[{"o":[v1,v2..],"a":0|1}...] } }
 * and each member <a.opt-label> has data-pg-member-id="<memberId>".
 *
 * Selection is read LIVE from the real variant-picker(s)
 * (.option-selector:not(.option-selector--product-group)) by data-option (name) +
 * checked value, matched to member option names case-insensitively. Same-locale on
 * both sides, so the strings line up; a mismatch just yields no bell (safe).
 */
(function () {
  if (window.__hpPgAvailabilityInit) return;
  window.__hpPgAvailabilityInit = true;

  // {optionNameLower: selectedValue} from the real variant picker(s) on the page.
  // Excludes single-value options (data-single-option="true", e.g. a degenerate
  // per-colour "Farbe"): they aren't a real cross-product axis, and matching on them
  // would wrongly require a member's colour to equal the current product's colour,
  // hiding genuine size sold-outs. Only the shared axis (e.g. Grösse) is matched.
  function currentSelection() {
    var sel = {};
    document.querySelectorAll('.option-selector:not(.option-selector--product-group):not([data-single-option="true"])').forEach(function (s) {
      var name = (s.getAttribute('data-option') || '').trim().toLowerCase();
      if (!name) return;
      var val = null;
      var radio = s.querySelector('.js-option:checked');
      if (radio) {
        val = radio.value;
      } else {
        var dd = s.querySelector('.custom-select__option[aria-selected="true"]');
        if (dd && dd.dataset.value !== '') val = dd.dataset.value;
      }
      if (val != null) sel[name] = val;
    });
    return sel;
  }

  function memberSoldOut(data, sel) {
    var names = data.names || [];
    var shared = [];
    for (var i = 0; i < names.length; i++) {
      var key = (names[i] || '').trim().toLowerCase();
      if (Object.prototype.hasOwnProperty.call(sel, key)) shared.push({ i: i, key: key });
    }
    if (!shared.length) return false; // no shared axis -> never bell
    var matching = (data.variants || []).filter(function (v) {
      return shared.every(function (s) { return String(v.o[s.i]) === String(sel[s.key]); });
    });
    if (!matching.length) return false; // member doesn't carry this combo -> under-show
    return matching.every(function (v) { return !v.a; }); // all matches sold out -> bell
  }

  function refresh() {
    var groups = document.querySelectorAll('.option-selector--product-group');
    if (!groups.length) return;
    var sel = currentSelection();
    groups.forEach(function (fs) {
      var dataEl = fs.querySelector('script[type="application/json"][data-pg-availability]');
      if (!dataEl) return;
      var map;
      try { map = JSON.parse(dataEl.textContent); } catch (e) { return; }
      fs.querySelectorAll('.opt-label[data-pg-member-id]').forEach(function (el) {
        var data = map[el.getAttribute('data-pg-member-id')];
        el.classList.toggle('hp-pg-oos', data ? memberSoldOut(data, sel) : false);
      });
    });
  }

  // Document-level delegation survives variant-picker.js replacing the picker element
  // on every variant change (the group fieldsets live OUTSIDE the picker and persist).
  function onChange(e) {
    var t = e.target;
    if (t && t.closest && t.closest('.option-selector:not(.option-selector--product-group)')) {
      setTimeout(refresh, 0);
    }
  }

  function start() {
    document.addEventListener('change', onChange);
    document.addEventListener('on:variant:change', function () { setTimeout(refresh, 0); });
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
